// University of California, Irvine: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC Irvine's Common Data Set 2025-2026 (2025-26-CDS-PDF-Upload.pdf,
//    linked as "2025-26" from Institutional Research, Assessment and
//    Planning). C1 gives Fall 2025 applicants, admits and enrollees with a
//    residency table. C8A answers no to using SAT or ACT scores. C21 and C22
//    answer no to early decision and early action. Its C16 check mark sits
//    between two options, so its decision date is not used; UCI's own dates
//    page gives it.
//  - UC Irvine admissions pages (dates and deadlines, first-year admission)
//    and the UC systemwide first-year dates page.
//  - Office of Financial Aid and Scholarships, estimated budgets for 2026-27
//    new undergraduates. The page's on-campus "Subtotal" reads $26,467, but
//    its items add to $24,917 and its published total ($45,206) matches the
//    items, so the items and total are used. The out-of-state total adds the
//    $39,270 the same page tells nonresidents to add.
//  - College Scorecard, school id 110653, read from the Scorecard school
//    page (the API's demo key was over its rate limit; same data).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Irvine Common Data Set 2025-2026',
        url: 'https://sites.uci.edu/irap/files/2026/05/2025-26-CDS-PDF-Upload.pdf',
        read: '2026-09-30',
    },
    dates: {
        label: 'UC Irvine Admissions, Dates and Deadlines',
        url: 'https://admissions.uci.edu/dates-deadlines.php',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, First-Year Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    tests: {
        label: 'UC Irvine Admissions, First-Year Admission',
        url: 'https://admissions.uci.edu/apply/first-year-students/index.php',
        read: '2026-09-30',
    },
    coa: {
        label: 'UC Irvine Financial Aid, New Student Budgets 2026-27',
        url: 'https://www.ofas.uci.edu/cost/undergraduate-costs/new-students.php',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110653-University-of-California-Irvine',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-irvine',
    kbId: 'university_of_california_irvine',
    name: 'University of California, Irvine',
    shortName: 'UC Irvine',
    location: 'Irvine, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UCI brand guidelines); decoration only.
    branding: {
        primary: '#255799',
        secondary: '#FECC07',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_irvine_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 124231,
        admitted: 35964,
        enrolled: 6421,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 86230, admitted: 18922 },
            { group: 'Out-of-state', applied: 16381, admitted: 7784 },
            { group: 'International', applied: 21620, admitted: 9258 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Irvine\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Irvine offers neither. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some arts majors ask for more than the UC application:',
        rows: [
            { school: 'School of the Arts: Dance, Bachelor of Music and Arts', requirement: 'An audition or portfolio is required, between December 1 and January 31. Your applicant portal gives your exact deadline.' },
            { school: 'School of the Arts: Drama', requirement: 'An optional portfolio or audition in your area of interest.' },
        ],
        source: 'dates',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application (one filing period)', deadline: 'November 30', decision: 'March 1 to 31', binding: false },
        ],
        note: 'You apply on the UC application, which opens August 1. UC Irvine posts first-year decisions in your applicant portal.',
        source: 'dates',
        binding: 'Nothing is binding. For fall 2027 the University of California has one filing period, October 1 to November 30, with admission decisions in March.',
        bindingSource: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Irvine is test-blind: SAT and ACT scores are not considered for admission or scholarships, even if you send them. After you enroll, scores may be used for class placement or some graduation requirements.',
        source: 'tests',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Systemwide fees', amounts: [15588, 15588] },
            { item: 'Campus fees', amounts: [4701, 4701] },
            { item: 'Nonresident tuition and fees', amounts: [0, 39270] },
            { item: 'Housing (on campus)', amounts: [12053, 12053] },
            { item: 'Food', amounts: [7323, 7323] },
            { item: 'Books and supplies', amounts: [1512, 1512] },
            { item: 'Personal', amounts: [2801, 2801] },
            { item: 'Transportation', amounts: [1228, 1228] },
        ],
        totals: [45206, 84476],
        note: 'The budget for a new student living on campus. UC Irvine publishes the in-state total; the out-of-state total adds the $39,270 the same page tells nonresidents to add.',
        source: 'coa',
        netPrice: {
            average: 13866,
            byIncome: [
                { income: '$0 to $30,000', amount: 6160 },
                { income: '$30,001 to $48,000', amount: 6609 },
                { income: '$48,001 to $75,000', amount: 9897 },
                { income: '$75,001 to $110,000', amount: 14658 },
                { income: 'Over $110,000', amount: 30831 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Irvine\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
